import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import ContentPasteSearchRoundedIcon from '@mui/icons-material/ContentPasteSearchRounded'
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import ImageRoundedIcon from '@mui/icons-material/ImageRounded'
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded'
import PlayCircleFilledWhiteRoundedIcon from '@mui/icons-material/PlayCircleFilledWhiteRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import {
  getDebugReportBundleUrl,
  getOwnerAdoptionRollup,
  getOwnerDashboardSummary,
  getOwnerOpsStatus,
  listOwnerActionLog,
  listOwnerCostBreakdown,
  listOwnerDebugReports,
  listOwnerFeedbackReports,
  listOwnerRevenueBreakdown,
  listOwnerSectionUsage,
  listOwnerWorkspaces,
  runOwnerAction,
  updateOwnerDebugReporterRestriction,
  updateOwnerDebugStatus,
  updateOwnerFeedbackStatus,
  type OwnerActionInput,
  type OwnerActionKind,
  type OwnerActionLogEntry,
  type OwnerAdoptionRow,
  type OwnerCostRow,
  type OwnerDebugReport,
  type OwnerDebugBundleUrlResult,
  type OwnerFeedbackReport,
  type OwnerMetric,
  type OwnerOpsRow,
  type OwnerRevenueRow,
  type OwnerSectionUsageRow,
  type OwnerTone,
  type OwnerWorkspaceRow,
} from '../../services/ownerConsoleService'
import { useAuth } from '../../context/useAuth'
import { useNls, NlsProvider } from '../../nls/nlsProvider'
import { ToastContext, type ToastVariant } from '../../context/toastContextCore'
import { firebaseAuth } from '../../services/firebase'
import { AppDialog } from '../Common/AppDialog'
import { OwnerSettingsPanel } from './panels/OwnerSettingsPanel'
import { OwnerTemplatesPanel } from './panels/OwnerTemplatesPanel'
import { OwnerLogsPanel } from './panels/OwnerLogsPanel'
import { OwnerActivityPanel } from './panels/OwnerActivityPanel'
import { OwnerSupportAccessPanel } from './panels/OwnerSupportAccessPanel'
import { OwnerCohortsPanel } from './panels/OwnerCohortsPanel'
import { OwnerFlagsPanel } from './panels/OwnerFlagsPanel'
import { OwnerSupportPanel } from './panels/OwnerSupportPanel'
import { OwnerPeoplePanel } from './panels/OwnerPeoplePanel'
import { OwnerHomePanel } from './panels/OwnerHomePanel'
import { OwnerWorkspacesListPanel } from './panels/OwnerWorkspacesPanel'
import { OwnerWorkspaceDetailPanel } from './panels/OwnerWorkspaceDetailPanel'
import { SupportAccessReasonProvider, useSupportAccessReason } from './shell/SupportAccessReasonDialog'
import { IntegrationsTieringPanel } from './panels/IntegrationsTieringPanel'
import { IntegrationsAuditPanel } from './panels/IntegrationsAuditPanel'
import { LinePolicyPanel } from './panels/LinePolicyPanel'
import { OwnerCannedRepliesManager, OwnerMacrosManager } from './panels/OwnerSupportPrimitivesConfig'
import { OwnerClientOutreachPanel } from './panels/OwnerClientOutreachPanel'
import { OwnerPlanLimitsPanel } from './panels/OwnerPlanLimitsPanel'
import { RepairPlaceAccessCard } from './panels/RepairPlaceAccessCard'
import './ownerConsole.css'
import './tokens.css'
import './shell/ownerShell.css'
import { OwnerRail } from './shell/OwnerRail'
import { OwnerHeader } from './shell/OwnerHeader'
import { OwnerNotBuiltYet } from './shell/OwnerNotBuiltYet'
import { OwnerCommandPalette, OwnerShortcutsSheet } from './kit/OwnerCommandPalette'
import { OwnerKitPlayground } from './kit/OwnerKitPlayground'
import { OwnerDemoLanePanel } from './panels/OwnerDemoLanePanel'
import { OwnerEmptyScaffold } from './kit/OwnerEmptyScaffold'
import { RolesProvider } from './context/RolesProvider'
import { useRoles, type OwnerRole, VALID_OWNER_ROLES } from './context/RolesContext'
import { SessionLeaseProvider, useSessionLease, isSessionStaleError } from './context/SessionLeaseProvider'
import { OwnerBreakGlassBanner, type ActiveBreakGlassSession } from './shell/OwnerBreakGlassBanner'
import { BreakGlassModal } from './dialogs/BreakGlassModal'
import { BLURRED_SCREENSHOT_PLACEHOLDER } from '../../services/ownerRedaction'
import { readBundleRaw, type BreakGlassReason } from '../../services/ownerConsoleService'
import {
  parseOwnerRoute,
  navigateOwner,
  type OwnerRoute,
} from './ownerRouter'
import { activeProjectId } from './shell/ownerEnv'

export type ConsoleTab = 'overview' | 'workspaces' | 'feedback' | 'debug' | 'revenue' | 'costs' | 'product' | 'operations' | 'settings' | 'integrations' | 'line' | 'templates' | 'controls'
type DataState = 'idle' | 'loading' | 'live' | 'partial' | 'unavailable'
type ClaimState = 'checking' | 'unauth' | 'granted' | 'denied' | 'error'

const tabs: Array<{ id: ConsoleTab; label: string; description: string }> = [
  { id: 'overview', label: 'Overview', description: 'Owner KPIs and current risks' },
  { id: 'workspaces', label: 'Workspaces', description: 'Details, analytics, workspace controls' },
  { id: 'feedback', label: 'Feedback', description: 'User ratings, remarks, skips' },
  { id: 'debug', label: 'Debug', description: 'Submitted debug sessions' },
  { id: 'revenue', label: 'Revenue', description: 'Subscriptions, seats, packs, add-ons' },
  { id: 'costs', label: 'Costs', description: 'AI, Firebase, storage, Functions' },
  { id: 'product', label: 'Product', description: 'Activation, retention, feature adoption' },
  { id: 'operations', label: 'Ops', description: 'Jobs, integrations, App Check, incidents' },
  { id: 'settings', label: 'Settings', description: 'Persistent owner configuration' },
  { id: 'integrations', label: 'Integrations', description: 'Cross-workspace connection health and audit' },
  { id: 'line', label: 'Line', description: 'Workspace AI policy controls' },
  { id: 'templates', label: 'Templates', description: 'Manage Explore templates' },
  { id: 'controls', label: 'Controls', description: 'Owner-only command surface' },
]

const isValidConsoleTab = (value: string | undefined): value is ConsoleTab =>
  tabs.some((tab) => tab.id === value)

type OwnerActionFieldKey = 'workspaceId' | 'userId' | 'plan' | 'creditAmount' | 'runId' | 'workflowId' | 'automationId' | 'enabled' | 'message'

type OwnerActionCatalogEntry = {
  kind: OwnerActionKind
  label: string
  target: string
  guard: string
  scope: 'workspace' | 'global' | 'user'
  destructive?: boolean
  readonly?: boolean
  driftWarning?: string
  shortcutTab?: ConsoleTab
  shortcutLabel?: string
  requiredRole: OwnerRole[]
  perRoleCaps?: Partial<Record<OwnerRole, Record<string, number> | null>>
  fields: Array<{ key: OwnerActionFieldKey; label: string; placeholder?: string; type?: 'text' | 'number' | 'select' | 'toggle' | 'textarea'; options?: string[]; min?: number; max?: number; step?: number }>
}

const OWNER_ACTION_CATALOG: OwnerActionCatalogEntry[] = [
  { kind: 'GRANT_WORKSPACE_CREDITS', label: 'Grant workspace credits', target: 'workspace_wallet/{workspaceId}', guard: 'Adds owner-issued credits directly to a workspace wallet id; audited.', scope: 'workspace', requiredRole: ['founder', 'support', 'finance'], perRoleCaps: { support: { creditAmount: 500 }, finance: null, founder: null }, fields: [
    { key: 'workspaceId', label: 'Workspace id', placeholder: 'workspaceId', type: 'text' },
    { key: 'creditAmount', label: 'Credits', placeholder: '100', type: 'number', min: 1, max: 100000, step: 25 },
  ] },
  { kind: 'CHANGE_PLAN', label: 'Change plan', target: 'workspaces.plan', guard: 'Rewrites subscription snapshot; audited.', scope: 'workspace', requiredRole: ['founder', 'finance'], fields: [
    { key: 'workspaceId', label: 'Workspace id', placeholder: 'workspaceId', type: 'text' },
    { key: 'plan', label: 'Plan', type: 'select', options: ['FREE_TIER', 'ESSENTIAL_TIER', 'GROWTH_TIER', 'PROFESSIONAL_TIER', 'ENTERPRISE_TIER'] },
  ] },
  { kind: 'SUSPEND_WORKSPACE', label: 'Suspend workspace', target: 'workspaces.ownerState', guard: 'Two-step: writes ownerState.suspended flag; audited.', scope: 'workspace', requiredRole: ['founder', 'support', 'finance'], fields: [
    { key: 'workspaceId', label: 'Workspace id', placeholder: 'workspaceId', type: 'text' },
    { key: 'enabled', label: 'Suspend?', type: 'toggle' },
  ] },
  { kind: 'FORCE_USAGE_REFRESH', label: 'Force usage refresh', target: 'workspace_usage_snapshot/{workspaceId}', guard: 'Re-computes and writes a snapshot; audited.', scope: 'workspace', requiredRole: ['founder', 'support', 'finance', 'readonly'], fields: [
    { key: 'workspaceId', label: 'Workspace id', placeholder: 'workspaceId', type: 'text' },
  ] },
  { kind: 'RETRY_FAILED_JOB', label: 'Retry failed job', target: 'automation_runs/{runId}', guard: 'Idempotent retry via automation engine; audited.', scope: 'workspace', requiredRole: ['founder', 'support', 'finance'], fields: [
    { key: 'workspaceId', label: 'Workspace id', placeholder: 'workspaceId', type: 'text' },
    { key: 'runId', label: 'Run id', placeholder: 'automation_runs doc id', type: 'text' },
    { key: 'workflowId', label: 'Workflow id (optional)', placeholder: 'automation_graphs doc id', type: 'text' },
  ] },
  { kind: 'SET_MAINTENANCE_MODE', label: 'Maintenance mode', target: 'system/config', guard: 'Persistent app-wide setting. Open Owner Settings to edit the toggle and banner.', scope: 'global', shortcutTab: 'settings', shortcutLabel: 'Open Settings', requiredRole: ['founder'], fields: [] },
  { kind: 'INSPECT_WORKSPACE', label: 'Inspect workspace', target: 'workspaces/{workspaceId} (read-only)', guard: 'Read-only counts across major collections.', scope: 'workspace', readonly: true, requiredRole: ['founder', 'support', 'finance', 'readonly'], fields: [
    { key: 'workspaceId', label: 'Workspace id', placeholder: 'workspaceId', type: 'text' },
  ] },
  { kind: 'RESET_WORKSPACE_CONTENT', label: 'Reset workspace content', target: 'workspaces/{workspaceId}.content', guard: 'DESTRUCTIVE - wipes checklists, forms, automations, notifications, activity, chat, AI, gamification ledgers, raids. Keeps users, wallet, workspace. Type the workspace NAME to confirm.', scope: 'workspace', destructive: true, requiredRole: ['founder'], fields: [
    { key: 'workspaceId', label: 'Workspace id', placeholder: 'workspaceId', type: 'text' },
  ] },
  { kind: 'RESET_CHAT_FOR_WORKSPACE', label: 'Reset chat (workspace)*', target: 'channels (workspaceId scope)', guard: 'DESTRUCTIVE - wipes messages, reactions, cursors. Keeps channel scaffold. Type workspace NAME to confirm.', scope: 'workspace', destructive: true, driftWarning: '* Chat is Phase 1 of 4; message subcollections may drift.', requiredRole: ['founder'], fields: [
    { key: 'workspaceId', label: 'Workspace id', placeholder: 'workspaceId', type: 'text' },
  ] },
  { kind: 'RESET_DESIGN_HUB_STALE', label: 'Reset design hub STALE*', target: 'design_hub (workspaceId scope)', guard: 'Clears STALE materialization markers so the adapter re-materializes from current design.', scope: 'workspace', destructive: true, driftWarning: '* Design Hub Bound Engine new (shipped 2026-08-11).', requiredRole: ['founder'], fields: [
    { key: 'workspaceId', label: 'Workspace id', placeholder: 'workspaceId', type: 'text' },
  ] },
]

const compactDate = (value: string) => {
  if (!value) return 'No timestamp'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'No timestamp'
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

const workspaceActionEntries = (role: OwnerRole = 'founder') =>
  OWNER_ACTION_CATALOG.filter((action) => action.scope === 'workspace' && action.requiredRole.includes(role))

const nonWorkspaceActionEntries = (role: OwnerRole = 'founder') =>
  OWNER_ACTION_CATALOG.filter((action) => action.scope !== 'workspace' && action.requiredRole.includes(role))

const statusTone = (status: string): OwnerTone => {
  const value = status.toUpperCase()
  if (value === 'NEW' || value === 'REOPENED') return 'warn'
  if (value === 'RESOLVED') return 'good'
  if (value === 'DISMISSED' || value === 'SKIPPED') return 'neutral'
  if (value === 'ACCEPTED' || value === 'TRIAGED' || value === 'IN_PROGRESS') return 'neutral'
  return 'neutral'
}

const PROJECT_TARGET_FLAG: Record<string, string> = {
  'timelining-app': '--dev',
  'timelining-sit': '--sit',
}

const OwnerConsoleInner = ({ subPath = '' }: { subPath?: string }) => {
  void isValidConsoleTab(subPath)
  const { currentUser, isAuthLoading, signInWithGoogle, signOutUser } = useAuth()
  const [leaseMinutes, setLeaseMinutes] = useState(30)
  const [claimState, setClaimState] = useState<ClaimState>('checking')
  const [claimError, setClaimError] = useState<string | null>(null)
  const [refreshBusy, setRefreshBusy] = useState(false)
  const [lastClaims, setLastClaims] = useState<Record<string, unknown> | null>(null)
  const [lastUid, setLastUid] = useState<string | null>(null)

  const handleForceRefresh = useCallback(async () => {
    const authUser = firebaseAuth.currentUser
    if (!authUser) {
      setClaimState('unauth')
      return
    }
    setRefreshBusy(true)
    setClaimError(null)
    try {
      await authUser.getIdToken(true)
      const token = await authUser.getIdTokenResult(true)
      setLastUid(authUser.uid)
      setLastClaims(token.claims as Record<string, unknown>)
      setClaimState(token.claims.systemAdmin === true ? 'granted' : 'denied')
    } catch (error) {
      console.info('[OwnerConsole] force refresh failed.', error)
      setClaimError('Token refresh failed. Try signing out and back in.')
      setClaimState('error')
    } finally {
      setRefreshBusy(false)
    }
  }, [])

  const [prevAuthLoading, setPrevAuthLoading] = useState(isAuthLoading)
  const [prevUserUid, setPrevUserUid] = useState(currentUser?.uid)
  if (prevAuthLoading !== isAuthLoading || prevUserUid !== currentUser?.uid) {
    setPrevAuthLoading(isAuthLoading)
    setPrevUserUid(currentUser?.uid)
    if (isAuthLoading) {
      setClaimState('checking')
    } else if (!currentUser) {
      setClaimState('unauth')
      setLastUid(null)
    } else {
      setClaimState('checking')
      setClaimError(null)
      setLastUid(currentUser.uid)
    }
  }

  useEffect(() => {
    if (isAuthLoading || !currentUser) {
      return
    }
    const authUser = firebaseAuth.currentUser ?? currentUser
    let cancelled = false
    if (!authUser || typeof authUser.getIdTokenResult !== 'function') {
      return
    }
    void authUser.getIdTokenResult(true)
      .then((token) => {
        if (cancelled) return
        const claims = (token?.claims ?? {}) as Record<string, unknown>
        setLastClaims(claims)
        setClaimState(claims.systemAdmin === true ? 'granted' : 'denied')
      })
      .catch((error) => {
        if (cancelled) return
        console.info('[OwnerConsole] failed to read id token claims.', error)
        setClaimError('Could not read your id-token claims. Try signing out and back in.')
        setClaimState('error')
      })
    return () => { cancelled = true }
  }, [currentUser, isAuthLoading])

  if (claimState === 'checking') {
    return (
      <main className="ownerConsole ownerConsoleLogin">
        <section className="ownerLoginPanel" aria-label="Owner console loading">
          <span className="ownerEyebrow">Owner console</span>
          <h1>Checking your access...</h1>
          <p>Verifying your Firebase session and systemAdmin claim.</p>
        </section>
      </main>
    )
  }

  if (claimState === 'unauth') {
    return (
      <main className="ownerConsole ownerConsoleLogin">
        <section className="ownerLoginPanel" aria-label="Owner console sign-in required">
          <span className="ownerEyebrow">Owner console</span>
          <h1>Sign in required</h1>
          <p>The Owner Console reads production data through system-admin callables. Sign in with the owner Google account, then reload this page.</p>
          <button type="button" onClick={() => void signInWithGoogle()}>Sign in with Google</button>
        </section>
      </main>
    )
  }

  if (claimState === 'denied' || claimState === 'error') {
    const projectId = activeProjectId()
    const targetFlag = PROJECT_TARGET_FLAG[projectId] ?? `--project ${projectId}`
    const emailForCommand = currentUser?.email ?? 'you@example.com'
    const suggestedCommand = `node functions/scripts/grantOwnerClaim.mjs ${emailForCommand} ${targetFlag}`
    const claimEntries = lastClaims
      ? Object.keys(lastClaims).filter((key) => !['iss', 'aud', 'auth_time', 'user_id', 'sub', 'iat', 'exp', 'email', 'email_verified', 'firebase'].includes(key))
      : []
    return (
      <main className="ownerConsole ownerConsoleLogin">
        <section className="ownerLoginPanel" aria-label="Owner console access denied">
          <span className="ownerEyebrow">Owner console</span>
          <h1>Owner access is not enabled for this account</h1>
          <p>
            {claimState === 'error'
              ? (claimError ?? 'Could not verify your id-token claims.')
              : 'You are signed in as an app user, but your account is missing the systemAdmin custom claim.'}
          </p>
          <p>
            Signed in as <strong>{currentUser?.email ?? currentUser?.uid ?? 'unknown'}</strong> against
            {' '}Firebase project <strong>{projectId}</strong>.
          </p>
          {lastUid ? (
            <p className="ownerHint">
              Session UID: <code>{lastUid}</code>
              <br />
              Extra token claims: <code>{claimEntries.length ? claimEntries.join(', ') : '(none — systemAdmin is missing)'}</code>
              <br />
              Compare the UID above with the <code>uid</code> printed by the grant script's JSON output — if they differ, the claim was granted to a different account. Common cause: a second Google account signed in on this project.
            </p>
          ) : null}
          <p className="ownerHint">Run <code>{suggestedCommand}</code> on a workstation with access to the project, then click Force refresh below (or sign out and back in).</p>
          <div className="ownerLoginActions">
            <button type="button" onClick={() => void handleForceRefresh()} disabled={refreshBusy}>
              {refreshBusy ? 'Refreshing…' : 'Force refresh token'}
            </button>
            <button type="button" onClick={() => void signOutUser()}>Sign out</button>
            <button type="button" onClick={() => window.location.reload()}>Reload</button>
          </div>
        </section>
      </main>
    )
  }

  const currentRole: OwnerRole =
    lastClaims && typeof lastClaims.ownerRole === 'string' && (VALID_OWNER_ROLES as readonly string[]).includes(lastClaims.ownerRole)
      ? (lastClaims.ownerRole as OwnerRole)
      : 'founder'
  const authTime = typeof lastClaims?.auth_time === 'number' ? (lastClaims.auth_time as number) : null

  return (
    <RolesProvider role={currentRole}>
      <SessionLeaseProvider
        authTime={authTime}
        leaseMinutes={leaseMinutes}
        onSignOut={signOutUser}
        onReauth={async () => {
          await signInWithGoogle()
          await handleForceRefresh()
        }}
      >
        <OwnerConsoleShell
          subPath={subPath}
          handleForceRefresh={handleForceRefresh}
          refreshBusy={refreshBusy}
          currentUser={currentUser}
          onLeaseMinutesLoaded={setLeaseMinutes}
        />
      </SessionLeaseProvider>
    </RolesProvider>
  )
}

export const OwnerConsole = ({ subPath = '' }: { subPath?: string }) => (
  <NlsProvider forcedLocale="en">
    <OwnerConsoleInner subPath={subPath} />
  </NlsProvider>
)

const OwnerConsoleShell = ({
  subPath = '',
  handleForceRefresh,
  refreshBusy,
  currentUser,
  onLeaseMinutesLoaded,
}: {
  subPath?: string
  handleForceRefresh: () => Promise<void>
  refreshBusy: boolean
  currentUser: ReturnType<typeof useAuth>['currentUser']
  onLeaseMinutesLoaded?: (mins: number) => void
}) => {
  const { remainingMinutes, triggerSessionStale } = useSessionLease()
  const [activeBreakGlass, setActiveBreakGlass] = useState<ActiveBreakGlassSession | null>(null)
  const [breakGlassModal, setBreakGlassModal] = useState<{
    open: boolean
    subject: string
    targetWorkspaceId?: string
    defaultReason?: BreakGlassReason
    defaultLinkedId?: string
  }>({
    open: false,
    subject: '',
  })

  const openBreakGlassModal = useCallback((
    subject: string,
    targetWorkspaceId?: string,
    defaultReason: BreakGlassReason = 'SUPPORT_TICKET',
    defaultLinkedId = '',
  ) => {
    setBreakGlassModal({
      open: true,
      subject,
      targetWorkspaceId,
      defaultReason,
      defaultLinkedId,
    })
  }, [])
  const [route, setRoute] = useState<OwnerRoute>(() =>
    parseOwnerRoute(
      subPath || (typeof window !== 'undefined' ? window.location.pathname : ''),
      typeof window !== 'undefined' ? window.location.search : '',
    ),
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => {
      setRoute(parseOwnerRoute(window.location.pathname, window.location.search))
    }
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])

  const [prevSubPath, setPrevSubPath] = useState(subPath)
  if (prevSubPath !== subPath) {
    setPrevSubPath(subPath)
    if (subPath) {
      setRoute(parseOwnerRoute(subPath, typeof window !== 'undefined' ? window.location.search : ''))
    }
  }

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('')

  const [homeRefreshNonce, setHomeRefreshNonce] = useState(0)
  const [homeLoading, setHomeLoading] = useState(false)
  const [homeSnapshotAt, setHomeSnapshotAt] = useState<Date | null>(null)
  const handleHomeSnapshotLoaded = useCallback((snap: { asOf: string }) => {
    setHomeSnapshotAt(new Date(snap.asOf))
  }, [])

  const [, setMetrics] = useState<OwnerMetric[] | null>(null)
  const [workspaces, setWorkspaces] = useState<OwnerWorkspaceRow[] | null>(null)
  const [feedback, setFeedback] = useState<OwnerFeedbackReport[] | null>(null)
  const [debugReports, setDebugReports] = useState<OwnerDebugReport[] | null>(null)
  const [sectionUsage, setSectionUsage] = useState<OwnerSectionUsageRow[] | null>(null)
  const [revenueRows, setRevenueRows] = useState<OwnerRevenueRow[] | null>(null)
  const [costRows, setCostRows] = useState<OwnerCostRow[] | null>(null)
  const [adoptionRows, setAdoptionRows] = useState<OwnerAdoptionRow[] | null>(null)
  const [opsRows, setOpsRows] = useState<OwnerOpsRow[] | null>(null)
  const [actionLog, setActionLog] = useState<OwnerActionLogEntry[] | null>(null)

  const [dataState, setDataState] = useState<DataState>('idle')
  const [dataMessage, setDataMessage] = useState('')
  const [sourcesFailed, setSourcesFailed] = useState<number | undefined>(undefined)
  const [sourcesTotal, setSourcesTotal] = useState<number | undefined>(undefined)
  const [snapshotAt, setSnapshotAt] = useState<Date | null>(null)
  const [isPaletteOpen, setPaletteOpen] = useState(false)
  const [isShortcutsOpen, setShortcutsOpen] = useState(false)

  const routeRef = useRef(route)
  useEffect(() => {
    routeRef.current = route
  })

  // Global Keyboard Shortcuts (⌘K, ?, g h, g w, g i, /, r, n, esc)
  useEffect(() => {
    let pendingG = false
    let gTimeout: NodeJS.Timeout | null = null

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const isInput =
        target &&
        (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
          target.isContentEditable)

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((prev) => !prev)
        return
      }

      if (isInput) return

      if (e.key === '?') {
        e.preventDefault()
        setShortcutsOpen(true)
        return
      }

      // Ticket detail shortcuts (r, n, s, t, p, #, esc) & list shortcuts (j, k, o, Enter, s, t, p, #)
      const currentRoute = routeRef.current
      const isSupportRoute = currentRoute.view === 'inbox' || currentRoute.view === 'ticket-detail'
      const hasSelectedTicket = Boolean(currentRoute.query?.selectedId || currentRoute.view === 'ticket-detail')

      if (isSupportRoute && hasSelectedTicket) {
        if (e.key.toLowerCase() === 'r') {
          e.preventDefault()
          const replyTab = document.querySelector<HTMLButtonElement>('.ownerSupportModeTab:first-child')
          replyTab?.click()
          const composeArea = document.getElementById('owner-support-compose-textarea') as HTMLTextAreaElement | null
          composeArea?.focus()
          return
        }
        if (e.key.toLowerCase() === 'n') {
          e.preventDefault()
          const internalTab = document.querySelector<HTMLButtonElement>('.ownerSupportModeTab:last-child')
          internalTab?.click()
          const composeArea = document.getElementById('owner-support-compose-textarea') as HTMLTextAreaElement | null
          composeArea?.focus()
          return
        }
        if (e.key.toLowerCase() === 's') {
          e.preventDefault()
          const snoozeBtn = document.getElementById('owner-support-snooze-btn') as HTMLButtonElement | null
          snoozeBtn?.click()
          return
        }
        if (e.key.toLowerCase() === 't') {
          e.preventDefault()
          const addTagBtn = document.getElementById('owner-support-add-tag-btn') as HTMLButtonElement | null
          addTagBtn?.click()
          return
        }
        if (e.key.toLowerCase() === 'p') {
          e.preventDefault()
          const prioritySelect = document.getElementById('owner-support-priority-select') as HTMLSelectElement | null
          prioritySelect?.focus()
          return
        }
        if (e.key === '#') {
          e.preventDefault()
          window.dispatchEvent(new CustomEvent('owner-support-key', { detail: { key: '#' } }))
          return
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          navigateOwner({ view: 'inbox', query: { ...currentRoute.query, selectedId: undefined } })
          return
        }
      } else if (isSupportRoute && !hasSelectedTicket) {
        if (['j', 'k', 'o', 'Enter', 's', 't', 'p', '#'].includes(e.key)) {
          e.preventDefault()
          window.dispatchEvent(new CustomEvent('owner-support-key', { detail: { key: e.key } }))
          return
        }
      }

      if (e.key === '/') {
        e.preventDefault()
        const searchInput = document.querySelector<HTMLInputElement>(
          '.ownerTableToolbar input, input[type="search"]',
        )
        if (searchInput) {
          searchInput.focus()
          searchInput.select()
        }
        return
      }

      if (e.key === 'g') {
        pendingG = true
        if (gTimeout) clearTimeout(gTimeout)
        gTimeout = setTimeout(() => {
          pendingG = false
        }, 1000)
        return
      }

      if (pendingG) {
        pendingG = false
        if (gTimeout) clearTimeout(gTimeout)
        if (e.key === 'h') {
          e.preventDefault()
          navigateOwner({ view: 'home' })
          return
        }
        if (e.key === 'i') {
          e.preventDefault()
          navigateOwner({ view: 'inbox' })
          return
        }
        if (e.key === 'w') {
          e.preventDefault()
          navigateOwner({ view: 'workspaces' })
          return
        }
      }
    }


    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      if (gTimeout) clearTimeout(gTimeout)
    }
  }, [])

  const isHomeRoute = route.view === 'home'
  const isActivityRoute = route.view === 'activity' || route.view === 'activity-detail'
  const isCohortsRoute = route.view === 'config-cohorts' || route.view === 'config-cohort-detail'
  const isFlagsRoute = route.view === 'config-flags' || route.view === 'config-flag-detail'

  const isExcludedRoute = isHomeRoute || isActivityRoute || isCohortsRoute || isFlagsRoute
  const [prevExcludedRoute, setPrevExcludedRoute] = useState(isExcludedRoute)
  if (prevExcludedRoute !== isExcludedRoute) {
    setPrevExcludedRoute(isExcludedRoute)
    if (isExcludedRoute) {
      setSourcesFailed(0)
      setSourcesTotal(0)
      setSnapshotAt(null)
      setDataState('idle')
      setDataMessage('')
    } else {
      setDataState('loading')
    }
  }

  const [refreshNonce, setRefreshNonce] = useState(0)

  useEffect(() => {
    if (isHomeRoute || isActivityRoute || isCohortsRoute || isFlagsRoute) {
      return
    }
    let cancelled = false
    const loadAll = async () => {
      const results = await Promise.allSettled([
        getOwnerDashboardSummary(),
        listOwnerWorkspaces(),
        listOwnerFeedbackReports(),
        listOwnerDebugReports(),
        listOwnerSectionUsage(),
        listOwnerRevenueBreakdown(),
        listOwnerCostBreakdown(),
        getOwnerAdoptionRollup(),
        getOwnerOpsStatus(),
        listOwnerActionLog(),
      ])
      if (cancelled) return
      if (
        routeRef.current.view === 'home' ||
        routeRef.current.view === 'activity' ||
        routeRef.current.view === 'activity-detail' ||
        routeRef.current.view === 'config-cohorts' ||
        routeRef.current.view === 'config-cohort-detail' ||
        routeRef.current.view === 'config-flags' ||
        routeRef.current.view === 'config-flag-detail'
      ) return
      const [summaryR, workspacesR, feedbackR, debugR, sectionR, revenueR, costR, adoptionR, opsR, logR] = results
      if (summaryR.status === 'fulfilled' && typeof summaryR.value.leaseMinutes === 'number') {
        onLeaseMinutesLoaded?.(summaryR.value.leaseMinutes)
      }
      setMetrics(summaryR.status === 'fulfilled' ? summaryR.value.metrics : null)
      setWorkspaces(workspacesR.status === 'fulfilled' ? workspacesR.value.rows : null)
      setFeedback(feedbackR.status === 'fulfilled' ? feedbackR.value.rows : null)
      setDebugReports(debugR.status === 'fulfilled' ? debugR.value.rows : null)
      setSectionUsage(sectionR.status === 'fulfilled' ? sectionR.value.rows : null)
      setRevenueRows(revenueR.status === 'fulfilled' ? revenueR.value.rows : null)
      setCostRows(costR.status === 'fulfilled' ? costR.value.rows : null)
      setAdoptionRows(adoptionR.status === 'fulfilled' ? adoptionR.value.rows : null)
      setOpsRows(opsR.status === 'fulfilled' ? opsR.value.rows : null)
      setActionLog(logR.status === 'fulfilled' ? logR.value.rows : null)
      if (workspacesR.status === 'fulfilled' && workspacesR.value.rows.length) {
        setSelectedWorkspaceId((current) => workspacesR.value.rows.some((row) => row.id === current) ? current : workspacesR.value.rows[0].id)
      }
      const failedCount = results.filter((result) => result.status === 'rejected').length
      const total = results.length
      setSourcesFailed(failedCount)
      setSourcesTotal(total)
      setSnapshotAt(new Date())

      const staleResult = results.find(
        (entry) => entry.status === 'rejected' && isSessionStaleError(entry.reason),
      )
      if (staleResult) {
        triggerSessionStale()
      }

      if (failedCount === 0) {
        setDataState('live')
        setDataMessage('')
      } else if (failedCount === total) {
        setDataState('unavailable')
        const first = results.find((entry) => entry.status === 'rejected') as PromiseRejectedResult | undefined
        const message = first?.reason instanceof Error ? first.reason.message : 'All owner sources failed.'
        setDataMessage(message)
      } else {
        setDataState('partial')
        const first = results.find((entry) => entry.status === 'rejected') as PromiseRejectedResult | undefined
        const reasonSuffix = first?.reason instanceof Error ? ` (${first.reason.message})` : ''
        setDataMessage(`${failedCount} of ${total} sources failed${reasonSuffix}`)
      }
    }
    void loadAll()
    return () => {
      cancelled = true
    }
  }, [triggerSessionStale, onLeaseMinutesLoaded, isHomeRoute, isActivityRoute, isCohortsRoute, isFlagsRoute, refreshNonce])

  const [prevWorkspaceDetailId, setPrevWorkspaceDetailId] = useState(
    route.view === 'workspace-detail' ? route.workspaceId : undefined,
  )
  if (
    route.view === 'workspace-detail' &&
    route.workspaceId &&
    prevWorkspaceDetailId !== route.workspaceId
  ) {
    setPrevWorkspaceDetailId(route.workspaceId)
    setSelectedWorkspaceId(route.workspaceId)
  }

  const setFeedbackStatus = useCallback((entry: OwnerFeedbackReport, status: string) => {
    setFeedback((current) => (current ?? []).map((row) => row.id === entry.id ? { ...row, status } : row))
    void updateOwnerFeedbackStatus({ id: entry.id, status }).catch((error) => {
      console.info('[OwnerConsole] feedback status update failed.', error)
    })
  }, [])

  const setDebugStatus = useCallback((entry: OwnerDebugReport, status: OwnerDebugReport['status'], note?: { ownerNote?: string; resolutionNote?: string }) => {
    setDebugReports((current) => (current ?? []).map((row) => row.id === entry.id ? {
      ...row,
      status,
      ownerNote: note?.ownerNote ?? row.ownerNote,
      resolutionNote: note?.resolutionNote ?? row.resolutionNote,
    } : row))
    void updateOwnerDebugStatus({ id: entry.id, status, ...note }).catch((error) => {
      console.info('[OwnerConsole] debug status update failed.', error)
    })
  }, [])

  const setDebugReporterRestriction = useCallback((entry: OwnerDebugReport, restriction: { blockNewTickets: boolean; blockReopenTickets: boolean; reason?: string }) => {
    setDebugReports((current) => (current ?? []).map((row) => row.reporterId === entry.reporterId ? {
      ...row,
      reporterRestriction: {
        userId: entry.reporterId,
        blockNewTickets: restriction.blockNewTickets,
        blockReopenTickets: restriction.blockReopenTickets,
        reason: restriction.reason ?? '',
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid ?? firebaseAuth.currentUser?.uid ?? 'owner',
      },
    } : row))
    void updateOwnerDebugReporterRestriction({
      userId: entry.reporterId,
      blockNewTickets: restriction.blockNewTickets,
      blockReopenTickets: restriction.blockReopenTickets,
      reason: restriction.reason,
    }).catch((error) => {
      console.info('[OwnerConsole] debug reporter restriction update failed.', error)
    })
  }, [currentUser?.uid])

  // Retained for BreakGlass and Phase 1a-2 seams
  void openBreakGlassModal
  void debugReports
  void setDebugStatus
  void setDebugReporterRestriction

  const submitOwnerAction = useCallback(async (input: OwnerActionInput) => {
    try {
      const result = await runOwnerAction(input)
      const log = await listOwnerActionLog()
      setActionLog(log.rows)
      return { ok: true as const, data: result.data }
    } catch (error) {
      if (isSessionStaleError(error)) {
        triggerSessionStale()
      }
      const message = error instanceof Error ? error.message : 'Owner action failed.'
      return { ok: false as const, message }
    }
  }, [triggerSessionStale])

  return (
    <SupportAccessReasonProvider activeBreakGlass={activeBreakGlass}>
      <div className="ownerConsoleLayout">
      <OwnerRail currentRoute={route} />
      <div className="ownerShellContainer">
        <OwnerHeader
          route={route}
          onRefresh={route.view === 'home' ? () => setHomeRefreshNonce((c) => c + 1) : () => setRefreshNonce((c) => c + 1)}
          refreshLoading={route.view === 'home' ? homeLoading : dataState === 'loading'}
          onForceRefresh={handleForceRefresh}
          refreshBusy={refreshBusy}
          snapshotAt={route.view === 'home' ? homeSnapshotAt : snapshotAt}
          dataState={route.view === 'home' ? (homeLoading ? 'loading' : homeSnapshotAt ? 'live' : 'idle') : dataState}
          dataMessage={dataMessage}
          sourcesFailed={sourcesFailed}
          sourcesTotal={sourcesTotal}
          sessionRemainingMinutes={remainingMinutes}
          onOpenCommandPalette={() => setPaletteOpen(true)}
          onNavigate={navigateOwner}
        />

        <OwnerBreakGlassBanner
          activeSession={activeBreakGlass}
          onSessionEnded={() => setActiveBreakGlass(null)}
          onSessionUpdated={setActiveBreakGlass}
          queryBg={route.query?.bg}
          queryFor={route.query?.for}
        />

        <main className="ownerMain">
          {route.view === 'home' && (
            <OwnerHomePanel
              onNavigate={navigateOwner}
              onLeaseMinutesLoaded={onLeaseMinutesLoaded}
              onLoadingChange={setHomeLoading}
              onSnapshotLoaded={handleHomeSnapshotLoaded}
              refreshNonce={homeRefreshNonce}
            />
          )}

          {(route.view === 'workspaces' || route.view === 'workspace-detail') && (
            <OwnerWorkspaceRoute
              actionLog={actionLog}
              dataState={dataState}
              onSubmit={submitOwnerAction}
              route={route}
              workspaces={workspaces}
            />
          )}

          {(route.view === 'feedback' || route.view === 'feedback-detail') && (
            <OwnerFeedbackPanel rows={feedback} onStatusChange={setFeedbackStatus} />
          )}

          {(route.view === 'inbox' || route.view === 'ticket-detail') && (
            <OwnerSupportPanel route={route} />
          )}

          {(route.view === 'people' || route.view === 'person-detail') && (
            <OwnerPeoplePanel route={route} />
          )}
          {/* Legacy panel wiring: onRestrictionChange={setDebugReporterRestriction} */}

          {route.view === 'revenue' && (
            <OwnerRevenuePanel rows={revenueRows} />
          )}

          {route.view === 'costs' && (
            <OwnerCostsPanel rows={costRows} />
          )}

          {route.view === 'analytics' && (
            <OwnerProductPanel adoptionRows={adoptionRows} sectionUsage={sectionUsage} />
          )}

          {route.view === 'ops' && (
            <OwnerOpsPanel rows={opsRows} />
          )}

          {(route.view === 'logs' || route.view === 'logs-detail') && (
            <OwnerLogsPanel route={route} />
          )}

          {(route.view === 'activity' || route.view === 'activity-detail') && (
            <OwnerActivityPanel route={route} />
          )}

          {route.view === 'support-access' && (
            <OwnerSupportAccessPanel />
          )}

          {(route.view === 'config-cohorts' || route.view === 'config-cohort-detail') && (
            <OwnerCohortsPanel route={route} />
          )}

          {(route.view === 'config-flags' || route.view === 'config-flag-detail') && (
            <OwnerFlagsPanel route={route} />
          )}

          {route.view === 'config-settings' && (
            <><OwnerSettingsPanel actorUid={currentUser?.uid ?? firebaseAuth.currentUser?.uid ?? 'owner'} /><IntegrationsTieringPanel actorUid={currentUser?.uid ?? firebaseAuth.currentUser?.uid ?? 'owner'} /></>
          )}

          {route.view === 'config-integrations' && (
            <IntegrationsAuditPanel actorUid={currentUser?.uid ?? firebaseAuth.currentUser?.uid ?? 'owner'} />
          )}

          {route.view === 'config-line' && (
            <LinePolicyPanel
              actorUid={currentUser?.uid ?? firebaseAuth.currentUser?.uid ?? 'owner'}
              workspaces={workspaces ?? []}
              selectedWorkspaceId={selectedWorkspaceId}
              onSelectWorkspace={setSelectedWorkspaceId}
            />
          )}

          {(route.view === 'config-templates' || route.view === 'config-template-detail') && (
            <OwnerTemplatesPanel actorUid={currentUser?.uid ?? firebaseAuth.currentUser?.uid ?? 'owner'} />
          )}

          {route.view === 'config-client-outreach' && (
            <OwnerClientOutreachPanel />
          )}

          {route.view === 'config-plan-limits' && (
            <OwnerPlanLimitsPanel />
          )}

          {route.view === 'config-actions' && (
            <OwnerControlsPanel
              actionLog={actionLog}
              onOpenTab={(tab) => {
                const tabRouteMap: Record<ConsoleTab, OwnerRoute> = {
                  overview: { view: 'home' },
                  workspaces: { view: 'workspaces' },
                  feedback: { view: 'feedback' },
                  debug: { view: 'inbox' },
                  revenue: { view: 'revenue' },
                  costs: { view: 'costs' },
                  product: { view: 'analytics' },
                  operations: { view: 'ops' },
                  settings: { view: 'config-settings' },
                  integrations: { view: 'config-integrations' },
                  line: { view: 'config-line' },
                  templates: { view: 'config-templates' },
                  controls: { view: 'config-actions' },
                }
                navigateOwner(tabRouteMap[tab] || { view: 'home' })
              }}
              onSubmit={submitOwnerAction}
            />
          )}

          {route.view === 'config-playground' && (
            <OwnerKitPlayground />
          )}

          {route.view === 'config-demo-lane' && (
            <OwnerDemoLanePanel />
          )}

          {[
            'people',
            'person-detail',
            'broadcast',
            'broadcast-new',
            'broadcast-detail',
            'incident-detail',
            'config-economy',
            'config-feedback-authoring',
            'config-feedback-authoring-detail',
            'config-experience-engine',
            'config-moderation',
          ].includes(route.view) && (
            <OwnerNotBuiltYet route={route} />
          )}
        </main>
      </div>
      <OwnerCommandPalette
        isOpen={isPaletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={navigateOwner}
      />
      <OwnerShortcutsSheet
        isOpen={isShortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />

      <BreakGlassModal
        open={breakGlassModal.open}
        subject={breakGlassModal.subject}
        targetWorkspaceId={breakGlassModal.targetWorkspaceId}
        defaultReason={breakGlassModal.defaultReason}
        defaultLinkedId={breakGlassModal.defaultLinkedId}
        onClose={() => setBreakGlassModal((prev) => ({ ...prev, open: false }))}
        onSuccess={(grant) => {
          setActiveBreakGlass({
            grantId: grant.grantId,
            revealToken: grant.revealToken,
            subject: grant.subject,
            reason: breakGlassModal.defaultReason || 'SUPPORT_TICKET',
            linkedId: breakGlassModal.defaultLinkedId || '',
            expiresAt: grant.expiresAt,
          })
        }}
      />
    </div>
    </SupportAccessReasonProvider>
  )
}


const OwnerFeedbackPanel = ({
  rows,
  onStatusChange,
}: {
  rows: OwnerFeedbackReport[] | null
  onStatusChange: (entry: OwnerFeedbackReport, status: string) => void
}) => (
  <section className="ownerPanel">
    <div className="ownerPanelHeader">
      <div>
        <span className="ownerEyebrow">Feedback inbox</span>
        <h2>User feedback management</h2>
        <p>Experience Engine feedback is mirrored into owner_feedback_reports for triage here.</p>
      </div>
    </div>
    {rows === null ? (
      <OwnerEmptyScaffold
        title="Feedback unavailable"
        body="User feedback reports could not be loaded."
        howDataGetsHere={[
          'Feedback is mirrored into owner feedback collections for triage.',
          'User remarks, ratings, and issue reports populate this surface.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : rows.length === 0 ? (
      <OwnerEmptyScaffold
        title="No feedback yet"
        body="No feedback submissions found for this project."
        howDataGetsHere={[
          'Feedback is mirrored into owner feedback collections for triage.',
          'User remarks, ratings, and issue reports populate this surface.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : (
      <div className="ownerInboxList">
        {rows.map((row) => (
          <article className="ownerInboxItem" key={row.id}>
            <div className="ownerInboxTopline">
              <div>
                <strong>{row.formId}</strong>
                <span>{row.reporterEmail} · {compactDate(row.submittedAt)}</span>
              </div>
              <span className={`ownerRisk ownerRisk--${statusTone(row.status)}`}>{row.status}</span>
            </div>
            <p>{row.skipped ? 'User skipped this feedback prompt.' : row.remark || 'No written remark.'}</p>
            <dl className="ownerMiniStats">
              <div><dt>Rating</dt><dd>{row.rating ?? 'Skipped'}</dd></div>
              <div><dt>Credits</dt><dd>{row.rewardCredits}</dd></div>
              <div><dt>User</dt><dd>{row.reporterId}</dd></div>
            </dl>
            <div className="ownerInlineActions">
              <button type="button" onClick={() => onStatusChange(row, 'TRIAGED')}>Triage</button>
              <button type="button" onClick={() => onStatusChange(row, 'IN_PROGRESS')}>Track</button>
              <button type="button" onClick={() => onStatusChange(row, 'RESOLVED')}>Resolve</button>
            </div>
          </article>
        ))}
      </div>
    )}
  </section>
)

type OwnerDebugFilter = 'ACTIVE' | 'RESOLVED' | 'ALL'

const activeDebugStatuses = new Set<OwnerDebugReport['status']>(['NEW', 'REOPENED', 'ACCEPTED', 'TRIAGED', 'IN_PROGRESS'])

const debugTicketTone = (row: OwnerDebugReport): OwnerTone => {
  if (row.severity === 'High' || row.errorCount > 0 || row.networkFailureCount > 0) return 'danger'
  if (row.severity === 'Medium' || row.warningCount > 0 || row.status === 'REOPENED') return 'warn'
  return 'good'
}

const detailValue = (value: string | number | boolean | null) => {
  if (value === null) return 'null'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

type OwnerDebugEventType = OwnerDebugReport['visibleEvents'][number]['type']

const ownerDebugEventTypes: OwnerDebugEventType[] = ['route', 'submit', 'error', 'console', 'network', 'screenshot', 'attachment', 'user_note']

const ownerDebugEventLabels: Record<OwnerDebugEventType, string> = {
  route: 'Route',
  submit: 'Submit',
  error: 'Error',
  console: 'Console',
  network: 'Network',
  screenshot: 'Screenshot',
  attachment: 'Attachment',
  user_note: 'Note',
}

const getOwnerDebugEventSearchText = (event: OwnerDebugReport['visibleEvents'][number]) =>
  `${event.summary} ${Object.values(event.details).map(detailValue).join(' ')}`.toLowerCase()

const filterOwnerDebugEvents = (
  events: OwnerDebugReport['visibleEvents'],
  selectedTypes: OwnerDebugEventType[],
  query: string,
) => {
  const normalizedQuery = query.trim().toLowerCase()
  return events.filter((event) => {
    if (selectedTypes.length > 0 && !selectedTypes.includes(event.type)) return false
    if (!normalizedQuery) return true
    return getOwnerDebugEventSearchText(event).includes(normalizedQuery)
  })
}

const ownerDebugEventKey = (event: OwnerDebugReport['visibleEvents'][number]) => `${event.id}-${event.type}-${event.at}`

export const OwnerDebugPanel = ({
  rows,
  onRestrictionChange,
  onStatusChange,
  activeBreakGlass,
  onOpenBreakGlass,
}: {
  rows: OwnerDebugReport[] | null
  onRestrictionChange: (entry: OwnerDebugReport, restriction: { blockNewTickets: boolean; blockReopenTickets: boolean; reason?: string }) => void
  onStatusChange: (entry: OwnerDebugReport, status: OwnerDebugReport['status'], note?: { ownerNote?: string; resolutionNote?: string }) => void
  activeBreakGlass?: ActiveBreakGlassSession | null
  onOpenBreakGlass?: (subject: string, targetWorkspaceId?: string, defaultReason?: BreakGlassReason, defaultLinkedId?: string) => void
}) => {
  const { t } = useNls()
  const { ensureAccessReason } = useSupportAccessReason()
  const toastContext = useContext(ToastContext)
  const showOwnerToast = useCallback((message: string, variant: ToastVariant = 'info') => {
    if (toastContext) {
      toastContext.showToast(message, variant)
    } else {
      console.info(`[OwnerConsole] ${message}`)
    }
  }, [toastContext])
  const [filter, setFilter] = useState<OwnerDebugFilter>('ACTIVE')
  const [selectedId, setSelectedId] = useState('')
  const [ownerNoteDraft, setOwnerNoteDraft] = useState('')
  const [resolutionNoteDraft, setResolutionNoteDraft] = useState('')
  const [restrictionReason, setRestrictionReason] = useState('')
  const [selectedEventTypes, setSelectedEventTypes] = useState<OwnerDebugEventType[]>([])
  const [eventSearchInput, setEventSearchInput] = useState('')
  const [debouncedEventSearch, setDebouncedEventSearch] = useState('')
  const [expandedEventIds, setExpandedEventIds] = useState<Record<string, boolean>>({})
  const [ticketJsonOpen, setTicketJsonOpen] = useState(false)
  const [screenshotStubOpen, setScreenshotStubOpen] = useState(false)
  const [bundleDownloading, setBundleDownloading] = useState(false)

  const tickets = useMemo(() => rows ?? [], [rows])
  const activeCount = tickets.filter((row) => activeDebugStatuses.has(row.status)).length
  const resolvedCount = tickets.filter((row) => row.status === 'RESOLVED' || row.status === 'DISMISSED').length
  const reopenedCount = tickets.filter((row) => row.status === 'REOPENED').length

  const filteredTickets = useMemo(() => {
    if (filter === 'ALL') return tickets
    if (filter === 'RESOLVED') return tickets.filter((row) => row.status === 'RESOLVED' || row.status === 'DISMISSED')
    return tickets.filter((row) => activeDebugStatuses.has(row.status))
  }, [filter, tickets])

  const selectedTicket = filteredTickets.find((row) => row.id === selectedId) ?? filteredTickets[0] ?? tickets[0]

  const [prevSelectedTicketId, setPrevSelectedTicketId] = useState<string | undefined>(selectedTicket?.id)
  if (prevSelectedTicketId !== selectedTicket?.id) {
    setPrevSelectedTicketId(selectedTicket?.id)
    if (!selectedTicket) {
      setSelectedId('')
    } else {
      setSelectedId(selectedTicket.id)
      setOwnerNoteDraft(selectedTicket.ownerNote)
      setResolutionNoteDraft(selectedTicket.resolutionNote)
      setRestrictionReason(selectedTicket.reporterRestriction?.reason ?? '')
    }
    setExpandedEventIds({})
  }

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedEventSearch(eventSearchInput), 200)
    return () => window.clearTimeout(timeout)
  }, [eventSearchInput])

  const visibleEvents = selectedTicket
    ? filterOwnerDebugEvents(selectedTicket.visibleEvents, selectedEventTypes, debouncedEventSearch)
    : []

  const ticketJson = selectedTicket ? JSON.stringify(selectedTicket, null, 2) : ''

  const toggleEventType = (type: OwnerDebugEventType) => {
    setSelectedEventTypes((current) => (
      current.includes(type)
        ? current.filter((entry) => entry !== type)
        : [...current, type]
    ))
  }

  const toggleEventJson = (eventKey: string) => {
    setExpandedEventIds((current) => ({ ...current, [eventKey]: !current[eventKey] }))
  }

  const copyText = async (text: string, successMessage: string) => {
    if (!navigator.clipboard?.writeText) {
      showOwnerToast('Clipboard is not available in this browser.', 'error')
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      showOwnerToast(successMessage, 'success')
    } catch (error) {
      showOwnerToast(error instanceof Error ? error.message : 'Copy failed.', 'error')
    }
  }

  const downloadBundle = async () => {
    if (!selectedTicket || !selectedTicket.hasDiagnostics || !selectedTicket.bundlePath) return
    setBundleDownloading(true)
    try {
      let result: OwnerDebugBundleUrlResult
      if (
        activeBreakGlass &&
        (activeBreakGlass.reason === 'INCIDENT' || activeBreakGlass.reason === 'ABUSE_REVIEW')
      ) {
        result = await readBundleRaw({
          ticketId: selectedTicket.id,
          grantId: activeBreakGlass.grantId,
          revealToken: activeBreakGlass.revealToken,
        })
      } else {
        const reason = await ensureAccessReason({
          key: `DEBUG_BUNDLE:${selectedTicket.id}`,
          defaultCode: 'SUPPORT_TICKET',
          defaultTicketId: selectedTicket.id,
        })
        if (!reason) {
          setBundleDownloading(false)
          return
        }
        result = await getDebugReportBundleUrl({ id: selectedTicket.id, accessReason: reason })
      }
      window.open(result.url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      showOwnerToast(error instanceof Error ? error.message : 'Could not open the diagnostic bundle.', 'error')
    } finally {
      setBundleDownloading(false)
    }
  }

  const setRestriction = (ticket: OwnerDebugReport, patch: { blockNewTickets?: boolean; blockReopenTickets?: boolean }) => {
    const current = ticket.reporterRestriction
    onRestrictionChange(ticket, {
      blockNewTickets: patch.blockNewTickets ?? current?.blockNewTickets ?? false,
      blockReopenTickets: patch.blockReopenTickets ?? current?.blockReopenTickets ?? false,
      reason: restrictionReason,
    })
  }

  const submitStatus = (status: OwnerDebugReport['status']) => {
    if (!selectedTicket) return
    onStatusChange(selectedTicket, status, {
      ownerNote: ownerNoteDraft,
      resolutionNote: status === 'RESOLVED' || status === 'DISMISSED' ? resolutionNoteDraft : selectedTicket.resolutionNote,
    })
  }

  return (
    <section className="ownerPanel ownerDebugConsole">
      <div className="ownerPanelHeader">
        <div>
          <span className="ownerEyebrow">Support inbox</span>
          <h2>Support ticket command center</h2>
          <p>User-submitted sessions stay queued here, move through owner acceptance and resolution, and sync status back to the reporter.</p>
        </div>
      </div>
      {rows === null ? (
        <OwnerEmptyScaffold
          title="Debug reports unavailable"
          body="Debug reports could not be loaded."
          howDataGetsHere={[
            'Debug sessions are recorded when users report issues.',
            'Diagnostics and event trails are bundled for triage.',
            'Check Firebase Functions logs if requests fail.',
          ]}
        />
      ) : rows.length === 0 ? (
        <OwnerEmptyScaffold
          title="No debug reports yet"
          body="No debug sessions submitted for this project."
          howDataGetsHere={[
            'Debug sessions are recorded when users report issues.',
            'Diagnostics and event trails are bundled for triage.',
            'Check Firebase Functions logs if requests fail.',
          ]}
        />
      ) : (
        <>
          <dl className="ownerMiniStats ownerMiniStats--debug ownerDebugOverview">
            <div><dt>Active</dt><dd>{activeCount}</dd></div>
            <div><dt>Reopened</dt><dd>{reopenedCount}</dd></div>
            <div><dt>Resolved</dt><dd>{resolvedCount}</dd></div>
            <div><dt>Total</dt><dd>{tickets.length}</dd></div>
          </dl>
          <div className="ownerDebugTabs" role="tablist" aria-label="Debug queue filters">
            {(['ACTIVE', 'RESOLVED', 'ALL'] as OwnerDebugFilter[]).map((value) => (
              <button
                key={value}
                type="button"
                className={filter === value ? 'isActive' : ''}
                onClick={() => setFilter(value)}
              >
                <ContentPasteSearchRoundedIcon fontSize="small" />
                {value === 'ACTIVE' ? 'Queue' : value === 'RESOLVED' ? 'Closed' : 'All'}
              </button>
            ))}
          </div>
          <div className="ownerDebugGrid">
            <div className="ownerDebugRail">
              {filteredTickets.length === 0 ? (
                <OwnerEmptyScaffold
                  title="No tickets in this view"
                  body="Switch filters to inspect closed or all debug tickets."
                />
              ) : filteredTickets.map((row) => (
                <button
                  type="button"
                  key={row.id}
                  className={`ownerDebugTicket ownerTone--${debugTicketTone(row)} ${selectedTicket?.id === row.id ? 'isSelected' : ''}`}
                  onClick={() => setSelectedId(row.id)}
                >
                  <span className="ownerDebugTicket__top">
                    <strong>{row.severity} · {row.status}</strong>
                    <small>{compactDate(row.updatedAt || row.createdAt)}</small>
                  </span>
                  <span>{row.reporterEmail}</span>
                  <small>{row.initialUrl || row.sessionId}</small>
                </button>
              ))}
            </div>
            {selectedTicket && (
              <>
                <article className={`ownerDebugStage ownerTone--${debugTicketTone(selectedTicket)}`}>
                  <div className="ownerDebugStageHeader">
                    <div>
                      <h3>{selectedTicket.title || 'Untitled support ticket'}</h3>
                      <div className="ownerInboxTopline">
                        <div>
                          <strong>{selectedTicket.id}</strong>
                          <span>{selectedTicket.reporterEmail} · {compactDate(selectedTicket.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="ownerDebugStageActions">
                      <div className="ownerDebugStageChips" aria-label="Ticket state">
                        <span className={`ownerRisk ownerRisk--${statusTone(selectedTicket.status)}`}>{selectedTicket.status}</span>
                        <span className={`ownerRisk ownerRisk--${debugTicketTone(selectedTicket)}`}>{selectedTicket.severity}</span>
                      </div>
                      <button type="button" onClick={() => setTicketJsonOpen(true)}>
                        <DataObjectRoundedIcon fontSize="small" />
                        View ticket JSON
                      </button>
                      {selectedTicket.hasDiagnostics && selectedTicket.bundlePath && (
                        <button
                          type="button"
                          className="ownerDebugBundleAction"
                          onClick={() => void downloadBundle()}
                          disabled={bundleDownloading}
                        >
                          <DownloadRoundedIcon fontSize="small" />
                          {bundleDownloading ? 'Opening...' : 'Download bundle'}
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="ownerDebugPrimaryMessage">{selectedTicket.description || selectedTicket.additionalMessage || selectedTicket.latestUserNote || 'No reporter message.'}</p>
                  {!selectedTicket.hasDiagnostics ? (
                    <OwnerEmptyScaffold
                      title="Written support request"
                      body="No diagnostics attached."
                    />
                  ) : (
                    <dl className="ownerMiniStats ownerMiniStats--debug">
                      <div><dt>Events</dt><dd>{selectedTicket.eventCount}</dd></div>
                      <div><dt>Errors</dt><dd>{selectedTicket.errorCount}</dd></div>
                      <div><dt>Warnings</dt><dd>{selectedTicket.warningCount}</dd></div>
                      <div><dt>Network</dt><dd>{selectedTicket.networkFailureCount}</dd></div>
                      <div><dt>Screens</dt><dd>{selectedTicket.screenshotCount}</dd></div>
                      <div><dt>Files</dt><dd>{selectedTicket.attachmentCount}</dd></div>
                      <div><dt>Reopens</dt><dd>{selectedTicket.reopenCount}</dd></div>
                      <div><dt>Email</dt><dd>{selectedTicket.emailDelivery}</dd></div>
                    </dl>
                  )}
                  <dl className="ownerDebugFacts">
                    <div><dt>User</dt><dd>{selectedTicket.reporterName || selectedTicket.reporterId}</dd></div>
                    <div><dt>Opened</dt><dd>{compactDate(selectedTicket.createdAt)}</dd></div>
                    <div><dt>Accepted</dt><dd>{selectedTicket.acceptedAt ? compactDate(selectedTicket.acceptedAt) : 'Pending'}</dd></div>
                    <div><dt>Resolved</dt><dd>{selectedTicket.resolvedAt ? compactDate(selectedTicket.resolvedAt) : 'Pending'}</dd></div>
                    <div><dt>Viewport</dt><dd>{selectedTicket.viewport || 'Unknown'}</dd></div>
                    <div><dt>Path</dt><dd>{selectedTicket.initialUrl || 'Unknown'}</dd></div>
                  </dl>
                  <div className="ownerDebugNoteGrid">
                    <label>
                      <span>Owner note</span>
                      <textarea value={ownerNoteDraft} onChange={(event) => setOwnerNoteDraft(event.target.value)} placeholder="Internal triage note" />
                    </label>
                    <label>
                      <span>Resolution note</span>
                      <textarea value={resolutionNoteDraft} onChange={(event) => setResolutionNoteDraft(event.target.value)} placeholder="What should the user see when this closes?" />
                    </label>
                  </div>
                  <div className="ownerInlineActions ownerInlineActions--debug">
                    <button type="button" onClick={() => submitStatus('ACCEPTED')}><CheckCircleRoundedIcon fontSize="small" />Accept</button>
                    <button type="button" onClick={() => submitStatus('IN_PROGRESS')}><PlayCircleFilledWhiteRoundedIcon fontSize="small" />Investigate</button>
                    <button type="button" onClick={() => submitStatus('RESOLVED')}><TaskAltRoundedIcon fontSize="small" />Resolve</button>
                    <button type="button" onClick={() => submitStatus('DISMISSED')}><BlockRoundedIcon fontSize="small" />Dismiss</button>
                  </div>
                  <div className="ownerDebugRestrictionControls">
                    <label>
                      <span>Restriction reason</span>
                      <input value={restrictionReason} onChange={(event) => setRestrictionReason(event.target.value)} placeholder="Visible only to owner console" />
                    </label>
                    <div>
                      <button
                        type="button"
                        className={selectedTicket.reporterRestriction?.blockNewTickets ? 'isActive' : ''}
                        onClick={() => setRestriction(selectedTicket, { blockNewTickets: !selectedTicket.reporterRestriction?.blockNewTickets })}
                      >
                        {selectedTicket.reporterRestriction?.blockNewTickets ? <LockOpenRoundedIcon fontSize="small" /> : <BlockRoundedIcon fontSize="small" />}
                        {selectedTicket.reporterRestriction?.blockNewTickets ? 'Allow new tickets' : 'Block new tickets'}
                      </button>
                      <button
                        type="button"
                        className={selectedTicket.reporterRestriction?.blockReopenTickets ? 'isActive' : ''}
                        onClick={() => setRestriction(selectedTicket, { blockReopenTickets: !selectedTicket.reporterRestriction?.blockReopenTickets })}
                      >
                        {selectedTicket.reporterRestriction?.blockReopenTickets ? <LockOpenRoundedIcon fontSize="small" /> : <BlockRoundedIcon fontSize="small" />}
                        {selectedTicket.reporterRestriction?.blockReopenTickets ? 'Allow reopens' : 'Block reopens'}
                      </button>
                    </div>
                  </div>
                  {selectedTicket.hasDiagnostics && (
                    <section className="ownerDebugEventList" aria-label="Owner-visible debug events">
                      <div>
                        <span className="ownerEyebrow">Visible event timeline</span>
                        <p>Only routes, submission events, errors, failed network calls, screenshots, attachments, and user notes are shown.</p>
                      </div>
                      <div className="ownerDebugEventFilters" aria-label="Visible event type filters">
                        <button
                          type="button"
                          className={selectedEventTypes.length === 0 ? 'isActive' : ''}
                          aria-pressed={selectedEventTypes.length === 0}
                          onClick={() => setSelectedEventTypes([])}
                        >
                          All
                        </button>
                        {ownerDebugEventTypes.map((type) => (
                          <button
                            key={type}
                            type="button"
                            className={selectedEventTypes.includes(type) ? 'isActive' : ''}
                            aria-pressed={selectedEventTypes.includes(type)}
                            onClick={() => toggleEventType(type)}
                          >
                            {ownerDebugEventLabels[type]}
                          </button>
                        ))}
                      </div>
                      <label className="ownerDebugEventSearch">
                        <SearchRoundedIcon fontSize="small" />
                        <input
                          aria-label="Search visible events"
                          value={eventSearchInput}
                          onChange={(event) => setEventSearchInput(event.target.value)}
                          placeholder="Search event summary or details"
                        />
                      </label>
                      {selectedTicket.visibleEvents.length === 0 ? (
                        <OwnerEmptyScaffold
                          title="No visible events"
                          body="This ticket did not include owner-visible event details."
                        />
                      ) : visibleEvents.length === 0 ? (
                        <OwnerEmptyScaffold
                          title="No matching events"
                          body="Adjust filters or search to inspect more diagnostic details."
                        />
                      ) : visibleEvents.map((event) => {
                        const eventKey = ownerDebugEventKey(event)
                        return (
                          <article key={`${selectedTicket.id}-${eventKey}`}>
                            <div>
                              <strong>{event.type}</strong>
                              <span>{compactDate(event.at)} · {event.screen || event.path || 'Unknown screen'}</span>
                            </div>
                            <p>{event.summary}</p>
                            <div className="ownerInlineActions ownerInlineActions--debug">
                              <button type="button" onClick={() => toggleEventJson(eventKey)}>
                                <DataObjectRoundedIcon fontSize="small" />
                                {expandedEventIds[eventKey] ? 'Collapse JSON' : 'Expand JSON'}
                              </button>
                             {event.type === 'screenshot' && (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <button type="button" className="ownerDebugScreenshotStub" onClick={() => setScreenshotStubOpen(true)}>
                                    <ImageRoundedIcon fontSize="small" />
                                    Preview
                                  </button>
                                  {activeBreakGlass && (activeBreakGlass.subject.includes(selectedTicket.id) || activeBreakGlass.subject.includes('screenshot')) && (
                                    <span className="ownerBreakGlassBadge">{t('NLSOwnerConsoleBreakGlassRevealedBadge')}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            {expandedEventIds[eventKey] && (
                              <div className="ownerDebugEventJson">
                                <button
                                  type="button"
                                  onClick={() => void copyText(JSON.stringify(event, null, 2), 'Event JSON copied.')}
                                >
                                  <ContentCopyRoundedIcon fontSize="small" />
                                  Copy JSON
                                </button>
                                <pre>{JSON.stringify(event, null, 2)}</pre>
                              </div>
                            )}
                          </article>
                        )
                      })}
                    </section>
                  )}
                </article>
                <AppDialog
                  open={ticketJsonOpen}
                  title="Ticket JSON"
                  message={<pre className="ownerDebugEventJson">{ticketJson}</pre>}
                  actions={[
                    { label: 'Copy JSON', variant: 'secondary', onClick: () => copyText(ticketJson, 'Ticket JSON copied.') },
                    { label: 'Close', variant: 'primary', onClick: () => setTicketJsonOpen(false), autoFocus: true },
                  ]}
                  onDismiss={() => setTicketJsonOpen(false)}
                />
                <AppDialog
                  open={screenshotStubOpen}
                  title="Screenshot preview"
                  message={
                    <div>
                      {activeBreakGlass && (activeBreakGlass.subject.includes(selectedTicket.id) || activeBreakGlass.subject.includes('screenshot')) ? (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span className="ownerBreakGlassBadge">{t('NLSOwnerConsoleBreakGlassRevealedBadge')}</span>
                          </div>
                          <img
                            src={BLURRED_SCREENSHOT_PLACEHOLDER}
                            alt="Revealed screenshot"
                            style={{ width: '100%', borderRadius: 6, display: 'block', marginBottom: 8 }}
                          />
                        </div>
                      ) : (
                        <div>
                          <p className="ownerDebugScreenshotStub">Preview requires the reporter's device; use bundle download.</p>
                          <img
                            src={BLURRED_SCREENSHOT_PLACEHOLDER}
                            alt="Blurred placeholder"
                            style={{ width: '100%', borderRadius: 6, display: 'block', margin: '8px 0' }}
                          />
                          {onOpenBreakGlass && (
                            <button
                              type="button"
                              className="ownerBreakGlassBanner__btn ownerBreakGlassBanner__btn--extend"
                              style={{ marginTop: 8 }}
                              onClick={() => {
                                setScreenshotStubOpen(false)
                                onOpenBreakGlass(`Ticket #${selectedTicket.id} screenshot`, undefined, 'SUPPORT_TICKET', selectedTicket.id)
                              }}
                            >
                              {t('NLSOwnerConsoleBreakGlassRevealButton')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  }
                  actions={[
                    { label: 'Close', variant: 'primary', onClick: () => setScreenshotStubOpen(false), autoFocus: true },
                  ]}
                  onDismiss={() => setScreenshotStubOpen(false)}
                />
              </>
            )}
          </div>
        </>
      )}
    </section>
  )
}

const OwnerRevenuePanel = ({ rows }: { rows: OwnerRevenueRow[] | null }) => (
  <section className="ownerPanel">
    <div className="ownerPanelHeader">
      <div>
        <span className="ownerEyebrow">Revenue</span>
        <h2>Subscription and commerce ledger</h2>
        <p>Derived from workspaces.plan × plan price, workspace_wallet lifetime purchases, and seat-count deltas. Marked Modeled until a Stripe event ledger exists.</p>
      </div>
    </div>
    {rows === null ? (
      <OwnerEmptyScaffold
        title="Revenue unavailable"
        body="Revenue breakdown could not be loaded."
        howDataGetsHere={[
          'Revenue figures are aggregated across workspace subscriptions and credit purchases.',
          'Transactions sync from payment processors.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : rows.length === 0 ? (
      <OwnerEmptyScaffold
        title="No revenue captured yet"
        body="No paid workspaces or credit-pack purchases have posted."
        howDataGetsHere={[
          'Revenue figures are aggregated across workspace subscriptions and credit purchases.',
          'Transactions sync from payment processors.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : (
      <div className="ownerRevenueGrid">
        {rows.map((row) => (
          <article key={row.key}>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
            <p>{row.detail}</p>
            <small>{row.source} · {row.confidence}</small>
          </article>
        ))}
      </div>
    )}
  </section>
)

const OwnerCostsPanel = ({ rows }: { rows: OwnerCostRow[] | null }) => (
  <section className="ownerPanel">
    <div className="ownerPanelHeader">
      <div>
        <span className="ownerEyebrow">Cost center</span>
        <h2>Modeled platform cost</h2>
        <p>Modeled from wallet reservations, storage/config.usedBytes, automation_runs count, analytics_events volume, field_passes. Replace unit prices in code when GCP billing rates change.</p>
      </div>
    </div>
    {rows === null ? (
      <OwnerEmptyScaffold
        title="Costs unavailable"
        body="Cost breakdown could not be loaded."
        howDataGetsHere={[
          'Cost signals are aggregated from cloud resources and AI inference providers.',
          'Daily rollup processes compute cost summaries.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : rows.length === 0 ? (
      <OwnerEmptyScaffold
        title="No cost signals yet"
        body="No wallet activity, storage usage, or function invocations were found."
        howDataGetsHere={[
          'Cost signals are aggregated from cloud resources and AI inference providers.',
          'Daily rollup processes compute cost summaries.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : (
      <div className="ownerCostGrid">
        {rows.map((row) => (
          <article className={`ownerCostCard ownerTone--${row.tone}`} key={row.key}>
            <span>{row.label}</span>
            <strong>{row.estimate}</strong>
            <p>{row.source}</p>
            <small>{row.confidence}</small>
          </article>
        ))}
      </div>
    )}
  </section>
)

const OwnerProductPanel = ({ adoptionRows, sectionUsage }: { adoptionRows: OwnerAdoptionRow[] | null; sectionUsage: OwnerSectionUsageRow[] | null }) => (
  <section className="ownerPanel">
    <div className="ownerPanelHeader">
      <div>
        <span className="ownerEyebrow">Product analytics</span>
        <h2>Feature adoption</h2>
        <p>Aggregated across workspace_usage/*/metrics — percentage of workspaces with non-zero usage per feature.</p>
      </div>
    </div>
    {adoptionRows === null ? (
      <OwnerEmptyScaffold
        title="Adoption unavailable"
        body="Feature adoption metrics could not be loaded."
        howDataGetsHere={[
          'Feature adoption metrics are compiled from workspace usage signals.',
          'User activity across boards and sections feeds into usage snapshots.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : adoptionRows.length === 0 ? (
      <OwnerEmptyScaffold
        title="No adoption signals yet"
        body="No feature usage signals recorded for any workspace."
        howDataGetsHere={[
          'Feature adoption metrics are compiled from workspace usage signals.',
          'User activity across boards and sections feeds into usage snapshots.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : (
      <div className="ownerAdoptionList">
        {adoptionRows.map((row) => (
          <article key={row.key}>
            <div>
              <strong>{row.label}</strong>
              <span>{row.detail}</span>
            </div>
            <div className="ownerProgressTrack" aria-label={`${row.label} adoption ${row.usage}%`}>
              <span style={{ width: `${row.usage}%` }} />
            </div>
            <b>{row.usage}%</b>
          </article>
        ))}
      </div>
    )}
    <div className="ownerPanelDivider" />
    <div className="ownerPanelHeader ownerPanelHeader--compact">
      <div>
        <span className="ownerEyebrow">Section usage</span>
        <h2>Most opened product sections</h2>
        <p>Tracked by ProductAnalyticsRouteBridge through the analytics_events callable path.</p>
      </div>
    </div>
    {sectionUsage === null ? (
      <OwnerEmptyScaffold
        title="Section usage unavailable"
        body="Section usage data could not be loaded."
        howDataGetsHere={[
          'Section navigation events track feature engagement across boards.',
          'Client telemetry events feed into analytics summaries.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : sectionUsage.length === 0 ? (
      <OwnerEmptyScaffold
        title="No section opens yet"
        body="No section navigation events recorded for this project."
        howDataGetsHere={[
          'Section navigation events track feature engagement across boards.',
          'Client telemetry events feed into analytics summaries.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : (
      <div className="ownerUsageList">
        {sectionUsage.map((row) => (
          <article key={row.sectionKey}>
            <div>
              <strong>{row.label}</strong>
              <span>{row.users} users · {row.workspaces} groups · last {compactDate(row.lastSeen)}</span>
            </div>
            <div className="ownerProgressTrack" aria-label={`${row.label} opens ${row.opens}`}>
              <span style={{ width: `${Math.min(100, Math.max(8, row.opens / Math.max(sectionUsage[0]?.opens ?? 1, 1) * 100))}%` }} />
            </div>
            <b>{row.opens.toLocaleString('en-IN')}</b>
          </article>
        ))}
      </div>
    )}
  </section>
)

const OwnerOpsPanel = ({ rows }: { rows: OwnerOpsRow[] | null }) => (
  <section className="ownerPanel">
    <div className="ownerPanelHeader">
      <div>
        <span className="ownerEyebrow">Operations</span>
        <h2>Reliability board</h2>
        <p>Live subsystem health from automation_runs, integration_sync_jobs, project_risk_events, ai_proposals.</p>
      </div>
    </div>
    {rows === null ? (
      <OwnerEmptyScaffold
        title="Ops status unavailable"
        body="Subsystem status could not be loaded."
        howDataGetsHere={[
          'Subsystem telemetry tracks queue depth, worker jobs, and operational alerts.',
          'Health signals report live operational metrics.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : rows.length === 0 ? (
      <OwnerEmptyScaffold
        title="All subsystems idle"
        body="No signals from any of the tracked subsystems."
        howDataGetsHere={[
          'Subsystem telemetry tracks queue depth, worker jobs, and operational alerts.',
          'Health signals report live operational metrics.',
          'Check Firebase Functions logs if requests fail.',
        ]}
      />
    ) : (
      <div className="ownerOpsList">
        {rows.map((row) => (
          <article className={`ownerTone--${row.tone}`} key={row.key}>
            <span>{row.subsystem}</span>
            <strong>{row.state}</strong>
            <p>{row.detail}</p>
          </article>
        ))}
      </div>
    )}
    <RepairPlaceAccessCard />
  </section>
)

/** Indexed list and exact-ID detail deliberately use separate data contracts. */
export const OwnerWorkspaceRoute = ({ onSubmit, route }: {
  actionLog: OwnerActionLogEntry[] | null
  dataState: DataState
  onSubmit: (input: OwnerActionInput) => Promise<{ ok: true; data?: Record<string, unknown> } | { ok: false; message: string }>
  route: Extract<OwnerRoute, { view: 'workspaces' | 'workspace-detail' }>
  workspaces: OwnerWorkspaceRow[] | null
}) => {
  if (route.view === 'workspaces') return <OwnerWorkspacesListPanel route={route} onSubmit={onSubmit} />

  return <OwnerWorkspaceDetailPanel route={route} onSubmit={onSubmit} />
}

const OwnerControlsPanel = ({
  actionLog,
  onOpenTab,
  onSubmit,
}: {
  actionLog: OwnerActionLogEntry[] | null
  onOpenTab: (tab: ConsoleTab) => void
  onSubmit: (input: OwnerActionInput) => Promise<{ ok: true; data?: Record<string, unknown> } | { ok: false; message: string }>
}) => {
  const { role } = useRoles()
  const visibleActions = nonWorkspaceActionEntries(role)
  const [activeSection, setActiveSection] = useState<'catalog' | 'canned' | 'macros'>('catalog')

  return (
    <section className="ownerControlsShell">
      <div className="ownerPanelHeader" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <span className="ownerEyebrow">Actions & runbooks</span>
          <h2>Owner commands and primitives</h2>
          <p>Workspace-specific controls now live on the Workspaces page so settings and analytics are reviewed before any workspace command runs. Global command catalog, canned replies, and multi-step macros.</p>
        </div>
        <div style={{ display: 'flex', gap: 6, background: 'var(--owner-bg-surface, #141824)', padding: 4, borderRadius: 6, border: '1px solid var(--owner-border-subtle, #1e293b)' }}>
          <button
            type="button"
            className={`ownerSupportHeaderActionBtn ${activeSection === 'catalog' ? 'ownerSupportModeTab--active' : ''}`}
            onClick={() => setActiveSection('catalog')}
          >
            Command Catalog
          </button>
          <button
            type="button"
            className={`ownerSupportHeaderActionBtn ${activeSection === 'canned' ? 'ownerSupportModeTab--active' : ''}`}
            onClick={() => setActiveSection('canned')}
          >
            Canned Replies
          </button>
          <button
            type="button"
            className={`ownerSupportHeaderActionBtn ${activeSection === 'macros' ? 'ownerSupportModeTab--active' : ''}`}
            onClick={() => setActiveSection('macros')}
          >
            Macros
          </button>
        </div>
      </div>

      {activeSection === 'catalog' && (
        <div className="ownerControlsBody">
          <article className="ownerPanel ownerControlsCatalog">
            <div className="ownerControlsIntro">
              <strong>{visibleActions.length} global/user actions</strong>
              <span>{workspaceActionEntries(role).length} workspace actions moved to the Workspaces page</span>
            </div>
            <div className="ownerControlGrid">
              {visibleActions.map((action) => (
                <OwnerActionCard
                  key={action.kind}
                  action={action}
                  selectedWorkspace={null}
                  onOpenTab={onOpenTab}
                  onSubmit={onSubmit}
                />
              ))}
            </div>
          </article>
          <aside className="ownerPanel ownerControlsLog">
            <span className="ownerEyebrow">Action log</span>
            <h2>owner_action_log</h2>
            {actionLog === null ? (
              <OwnerEmptyScaffold
                title="Log unavailable"
                body="Action log could not be loaded."
                howDataGetsHere={[
                  'Owner actions dispatched from the console create immutable audit log entries.',
                  'Signature verification is not available for historical records.',
                  'Check Firebase Functions logs if requests fail.',
                ]}
              />
            ) : actionLog.length === 0 ? (
              <OwnerEmptyScaffold
                title="No owner actions yet"
                body="Nothing has been dispatched from this console."
                howDataGetsHere={[
                  'Every administrative action performed in the Owner Console is recorded in the immutable audit log.',
                  'Actions record who ran the command, timestamps, and parameters.',
                  'Check Firebase Functions logs if actions fail.',
                ]}
              />
            ) : (
              <OwnerActionLogList entries={actionLog} />
            )}
          </aside>
        </div>
      )}

      {activeSection === 'canned' && (
        <article className="ownerPanel" style={{ padding: 20 }}>
          <OwnerCannedRepliesManager />
        </article>
      )}

      {activeSection === 'macros' && (
        <article className="ownerPanel" style={{ padding: 20 }}>
          <OwnerMacrosManager />
        </article>
      )}
    </section>
  )
}

const OwnerActionLogList = ({ entries }: { entries: OwnerActionLogEntry[] }) => (
  <div className="ownerActionLog">
    {entries.map((entry) => (
      <article key={entry.id}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>{entry.kind.replaceAll('_', ' ')}</strong>
          {entry.status && (
            <small className={`ownerStatusChip ownerStatusChip--${entry.status === 'SUCCESS' ? 'good' : entry.status === 'FAILED' ? 'danger' : 'neutral'}`}>
              {entry.status}
            </small>
          )}
        </div>
        <span>{entry.targetLabel}</span>
        <p>{entry.reason}</p>
        <small>
          {entry.actorEmail || entry.actorId}
          {entry.actorRole ? ` (${entry.actorRole})` : ''}
          {entry.environment ? ` · ${entry.environment}` : ''}
          {` · ${compactDate(entry.createdAt)}`}
        </small>
        {entry.errorMessage ? <em>{entry.errorMessage}</em> : null}
      </article>
    ))}
  </div>
)

const targetForAction = (action: OwnerActionCatalogEntry, selectedWorkspace: OwnerWorkspaceRow | null) =>
  selectedWorkspace ? action.target.replaceAll('{workspaceId}', selectedWorkspace.id) : action.target

const shouldUseSelectedWorkspaceField = (action: OwnerActionCatalogEntry, key: OwnerActionFieldKey) =>
  action.scope === 'workspace' && key === 'workspaceId'

const OwnerActionCard = ({
  action,
  selectedWorkspace,
  onOpenTab,
  onSubmit,
}: {
  action: OwnerActionCatalogEntry
  selectedWorkspace: OwnerWorkspaceRow | null
  onOpenTab: (tab: ConsoleTab) => void
  onSubmit: (input: OwnerActionInput) => Promise<{ ok: true; data?: Record<string, unknown> } | { ok: false; message: string }>
}) => {
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [reason, setReason] = useState('')
  const [confirmPhrase, setConfirmPhrase] = useState('')
  const [pending, setPending] = useState(false)
  const [result, setResult] = useState<{ tone: OwnerTone; text: string; data?: Record<string, unknown> } | null>(null)

  const destructiveExpected = action.destructive && selectedWorkspace ? selectedWorkspace.name : ''

  const handleSubmit = async () => {
    setPending(true)
    setResult(null)
    const input: OwnerActionInput = {
      kind: action.kind,
      reason: (reason.trim() || (action.readonly ? 'read-only inspect' : '')),
    }
    if (action.scope === 'workspace' && selectedWorkspace) input.workspaceId = selectedWorkspace.id
    for (const field of action.fields) {
      if (shouldUseSelectedWorkspaceField(action, field.key)) continue
      const raw = values[field.key]
      if (field.type === 'toggle') {
        (input as Record<string, unknown>)[field.key] = raw === true
      } else if (field.type === 'number') {
        const parsed = Number(raw)
        if (Number.isFinite(parsed)) (input as Record<string, unknown>)[field.key] = parsed
      } else if (typeof raw === 'string' && raw.trim()) {
        (input as Record<string, unknown>)[field.key] = raw.trim()
      }
    }
    if (destructiveExpected) input.confirmPhrase = confirmPhrase.trim()
    const response = await onSubmit(input)
    setPending(false)
    if (response.ok) {
      setResult({
        tone: 'good',
        text: `${action.label} dispatched.`,
        data: response.data,
      })
      setReason('')
      setConfirmPhrase('')
    } else {
      setResult({ tone: 'danger', text: response.message })
    }
  }

  if (action.shortcutTab) {
    return (
      <article className="ownerActionCard ownerActionCard--shortcut">
        <span className="ownerActionTarget">{action.target}</span>
        <strong>{action.label}</strong>
        <p>{action.guard}</p>
        <button type="button" onClick={() => onOpenTab(action.shortcutTab!)}>
          {action.shortcutLabel ?? 'Open surface'}
        </button>
      </article>
    )
  }

  return (
    <article className={`ownerActionCard ${action.destructive ? 'isDestructive' : ''} ${action.readonly ? 'isReadonly' : ''}`}>
      <span className="ownerActionTarget">{action.target}</span>
      <strong>{action.label}</strong>
      <span>{targetForAction(action, selectedWorkspace)}</span>
      <p>{action.guard}</p>
      {action.scope === 'user' ? <p className="ownerActionScopeHint">This action is per-user, workspace selection ignored.</p> : null}
      {action.driftWarning ? <p className="ownerActionDrift">{action.driftWarning}</p> : null}
      <div className="ownerActionFields">
        {action.fields.map((field) => {
          if (shouldUseSelectedWorkspaceField(action, field.key)) return null
          if (field.type === 'select') {
            return (
              <label key={field.key}>
                <span>{field.label}</span>
                <select
                  value={typeof values[field.key] === 'string' ? String(values[field.key]) : ''}
                  onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
                >
                  <option value="">Select...</option>
                  {(field.options ?? []).map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
            )
          }
          if (field.type === 'toggle') {
            return (
              <label key={field.key}>
                <span>{field.label}</span>
                <input
                  type="checkbox"
                  checked={values[field.key] === true}
                  onChange={(event) => setValues({ ...values, [field.key]: event.target.checked })}
                />
              </label>
            )
          }
          if (field.type === 'textarea') {
            return (
              <label key={field.key}>
                <span>{field.label}</span>
                <textarea
                  placeholder={field.placeholder}
                  value={typeof values[field.key] === 'string' ? String(values[field.key]) : ''}
                  onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
                />
              </label>
            )
          }
          return (
            <label key={field.key}>
              <span>{field.label}</span>
              <input
                type={field.type ?? 'text'}
                placeholder={field.placeholder}
                value={typeof values[field.key] === 'string' || typeof values[field.key] === 'number' ? String(values[field.key]) : ''}
                min={field.min}
                max={field.max}
                step={field.step}
                onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
              />
            </label>
          )
        })}
        {!action.readonly ? (
          <label>
            <span>Reason (required)</span>
            <textarea
              value={reason}
              placeholder="Why is this action being taken?"
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
        ) : null}
        {destructiveExpected ? (
          <label>
            <span>Type workspace name to confirm ({destructiveExpected || 'select a workspace first'})</span>
            <input
              type="text"
              value={confirmPhrase}
              placeholder={destructiveExpected}
              onChange={(event) => setConfirmPhrase(event.target.value)}
            />
          </label>
        ) : null}
      </div>
      <button
        type="button"
        disabled={pending || (Boolean(destructiveExpected) && confirmPhrase.trim() !== destructiveExpected)}
        onClick={() => void handleSubmit()}
      >
        {pending ? 'Working...' : action.readonly ? 'Load' : action.destructive ? 'Confirm destructive action' : 'Confirm action'}
      </button>
      {result ? (
        <div className={`ownerActionResult ownerTone--${result.tone}`}>
          <p>{result.text}</p>
          {result.data ? (
            <pre className="ownerActionResultData">{JSON.stringify(result.data, null, 2)}</pre>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}

export { HosPatientLegalSetupCard } from './panels/HosPatientLegalSetupCard'

export default OwnerConsole
